import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

// Genre configuration with accent colors
const GENRE_COLORS: Record<string, string> = {
  electronic: 'from-violet-500/30 to-purple-600/20 border-violet-500/30 hover:border-violet-400/50',
  ambient: 'from-cyan-500/30 to-teal-600/20 border-cyan-500/30 hover:border-cyan-400/50',
  rock: 'from-red-500/30 to-rose-600/20 border-red-500/30 hover:border-red-400/50',
  jazz: 'from-amber-500/30 to-orange-600/20 border-amber-500/30 hover:border-amber-400/50',
  classical: 'from-pink-500/30 to-rose-600/20 border-pink-500/30 hover:border-pink-400/50',
  hiphop: 'from-yellow-500/30 to-amber-600/20 border-yellow-500/30 hover:border-yellow-400/50',
  'hip-hop': 'from-yellow-500/30 to-amber-600/20 border-yellow-500/30 hover:border-yellow-400/50',
  pop: 'from-pink-400/30 to-fuchsia-500/20 border-pink-400/30 hover:border-pink-300/50',
  indie: 'from-indigo-500/30 to-violet-600/20 border-indigo-500/30 hover:border-indigo-400/50',
  lofi: 'from-purple-400/30 to-indigo-500/20 border-purple-400/30 hover:border-purple-300/50',
  'lo-fi': 'from-purple-400/30 to-indigo-500/20 border-purple-400/30 hover:border-purple-300/50',
  house: 'from-orange-500/30 to-red-600/20 border-orange-500/30 hover:border-orange-400/50',
  techno: 'from-blue-500/30 to-cyan-600/20 border-blue-500/30 hover:border-blue-400/50',
  chill: 'from-sky-400/30 to-blue-500/20 border-sky-400/30 hover:border-sky-300/50',
  soul: 'from-orange-400/30 to-amber-500/20 border-orange-400/30 hover:border-orange-300/50',
  rnb: 'from-rose-400/30 to-pink-500/20 border-rose-400/30 hover:border-rose-300/50',
  'r&b': 'from-rose-400/30 to-pink-500/20 border-rose-400/30 hover:border-rose-300/50',
  metal: 'from-zinc-500/30 to-slate-600/20 border-zinc-500/30 hover:border-zinc-400/50',
  country: 'from-amber-400/30 to-yellow-500/20 border-amber-400/30 hover:border-amber-300/50',
  folk: 'from-emerald-500/30 to-green-600/20 border-emerald-500/30 hover:border-emerald-400/50',
  blues: 'from-blue-600/30 to-indigo-700/20 border-blue-600/30 hover:border-blue-500/50',
  reggae: 'from-green-500/30 to-yellow-600/20 border-green-500/30 hover:border-green-400/50',
  default: 'from-white/10 to-white/5 border-white/20 hover:border-white/30',
};

interface GenreFiltersProps {
  genres: [string, number][];
  selectedGenres: string[];
  onToggleGenre: (genre: string) => void;
  onClearAll: () => void;
}

export function GenreFilters({ 
  genres, 
  selectedGenres, 
  onToggleGenre,
  onClearAll,
}: GenreFiltersProps) {
  if (genres.length === 0) return null;

  const getColorClass = (genre: string) => {
    const key = genre.toLowerCase().replace(/\s+/g, '');
    return GENRE_COLORS[key] || GENRE_COLORS.default;
  };

  return (
    <div className="px-6 py-3">
      <div className="flex items-center gap-2 flex-wrap">
        {/* Clear all button - only show when filters are active */}
        {selectedGenres.length > 0 && (
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            onClick={onClearAll}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white/10 text-white hover:bg-white/20 transition-all border border-white/10"
          >
            <X className="h-3 w-3" />
            Clear
          </motion.button>
        )}

        {/* Genre pills */}
        {genres.map(([genre, count], index) => {
          const isSelected = selectedGenres.includes(genre.toLowerCase());
          const colorClass = getColorClass(genre);
          
          return (
            <motion.button
              key={genre}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.02 }}
              onClick={() => onToggleGenre(genre.toLowerCase())}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium transition-all border backdrop-blur-md',
                'bg-gradient-to-r',
                colorClass,
                isSelected 
                  ? 'ring-2 ring-white/30 ring-offset-1 ring-offset-background shadow-lg' 
                  : 'opacity-80 hover:opacity-100'
              )}
            >
              <span className="capitalize">{genre}</span>
              <span className="ml-1.5 opacity-60">{count}</span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
